import { forwardRef, ReactNode, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { CustomContentProps } from 'notistack';
import { useAtom } from 'jotai';
import Banner, { BannerProps } from './Banner';
import useLazySnackbar from './useLazySnackbar';
import { isSnackbarInitializedAtom } from './store';

const SnackbarProvider = dynamic<any>(() => import('notistack').then((notistack) => notistack.SnackbarProvider), {
  ssr: false,
});

declare module 'notistack' {
  interface VariantOverrides {
    // removes the `warning` variant
    warning: false;
    // adds onClick to the `error` variant
    error: { onClick?: () => void; action?: ReactNode };
    // adds onClick to the `success` variant
    success: { onClick?: () => void; action?: ReactNode };
  }
}

interface NotistackBannerProps extends Omit<CustomContentProps, 'action'> {
  onClick?: () => void;
  action?: ReactNode;
}

const createNotistackBanner = (variant: BannerProps['variant'], SnackbarContent) =>
  forwardRef<HTMLDivElement, NotistackBannerProps>((props, ref) => {
    const { closeSnackbar } = useLazySnackbar();

    const { id, message, onClick, action, style, className } = props;

    return (
      <SnackbarContent ref={ref} role="alert" data-testid="warning-banner" style={style} className={className}>
        <Banner variant={variant} onDismiss={() => closeSnackbar(id)} onClick={onClick} action={action}>
          {message}
        </Banner>
      </SnackbarContent>
    );
  });

const getSnackbarComponents = (SnackbarContent) => ({
  error: createNotistackBanner('error', SnackbarContent),
  success: createNotistackBanner('success', SnackbarContent),
});

export default function CustomSnackbarProvider({ initializedOverride = false }) {
  const [isSnackbarInitialized, setIsSnackbarInitialized] = useAtom(isSnackbarInitializedAtom);
  const [snackbarComponents, setSnackbarComponents] = useState(undefined);

  // if the snackbar is initialized, then we need to set the snackbarComponents
  useEffect(() => {
    (async () => {
      if (isSnackbarInitialized) {
        const { SnackbarContent } = await import('notistack');
        const components = getSnackbarComponents(SnackbarContent);
        setSnackbarComponents(components);
      }
    })();
  }, [isSnackbarInitialized]);

  // if the snackbar is initialized by initializedOverride, then we need to set the isSnackbarInitialized to true
  useEffect(() => {
    if (!isSnackbarInitialized && initializedOverride) {
      setIsSnackbarInitialized(true);
    }
  }, [isSnackbarInitialized, initializedOverride, setIsSnackbarInitialized]);

  // if the snackbar is not initialized, then we need to return null
  if (!initializedOverride && !isSnackbarInitialized && !snackbarComponents) {
    return null;
  }

  return (
    <SnackbarProvider
      domRoot={typeof window !== 'undefined' && document.getElementById('notistackPortal')}
      classes={{
        containerRoot: 'snackbar-container',
      }}
      Components={snackbarComponents}
      autoHideDuration={15000}
      maxSnack={3}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    />
  );
}
